import EventPublication from '../components/EventPublication';
import { safeLocalStorage } from '../lib/safeStorage';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, MapPin, Users, Ticket, Tag, Layers, Loader2, ClipboardList } from 'lucide-react';
import { ProjectCompletionForm } from '../components/ProjectCompletionForm';
import { EVENT_TYPE_LABELS, STATUS_LABELS, FIELD_LABELS } from '../constants/canonicalEnums';
import { MATCH_FEATURE_NAME } from '../config/brand';
import { projectSearchPrefill } from '../lib/projectSearch';

const API = process.env.REACT_APP_BACKEND_URL;
const STAGE_LABELS = { idea: 'Fikir', draft: 'Taslak', matching: MATCH_FEATURE_NAME };
export const LAST_PROJECT_KEY = 'mekandapro:lastProjectId';

const Field = ({ icon: Icon, label, value, testId }) => (
  <div data-testid={testId}>
    <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 flex items-center gap-1"><Icon size={11} /> {label}</p>
    <p className="text-sm text-zinc-800 mt-0.5">{value || '—'}</p>
  </div>
);

const OrganizerProjectDetail = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [completing, setCompleting] = useState(false);
  const navigate = useNavigate();
  const goToMatching = (p) => navigate(`/organizer/search?project=${p.id}`, { state: { prefill: projectSearchPrefill(p) } });

  const sendToMatching = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/api/organizer/event-projects/${projectId}/send-to-matching`);
      setProject(data.project);
      toast.success(data.already_linked ? 'Projen mekân aramaya hazır.' : 'Projen mekân aramaya hazır.');
      goToMatching(data.project);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Mekân aramaya geçilemedi.');
    } finally { setSending(false); }
  };

  useEffect(() => {
    axios.get(`${API}/api/organizer/event-projects/${projectId}`)
      .then(({ data }) => { setProject(data); safeLocalStorage.setItem(LAST_PROJECT_KEY, data.id); })
      .catch(err => setError(err.response?.data?.detail || 'Proje yüklenemedi.'));
  }, [projectId]);

  if (error) return <div className="max-w-3xl mx-auto px-6 py-12 text-sm text-red-700" data-testid="project-detail-error">{error}</div>;
  if (!project) return <div className="max-w-3xl mx-auto px-6 py-12 text-sm text-zinc-400">Yükleniyor…</div>;

  const m = project.matching;
  const missing = m?.missing_fields || [];
  const needsCompletion = !!m && (missing.length > 0 || m.status === 'bilgi_eksik');
  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="project-detail">
      <Link to="/organizer/home" className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 mb-5" data-testid="project-detail-back">
        <ArrowLeft size={13} /> Ana Sayfa
      </Link>
      <button className="action-secondary mb-4" onClick={() => setCompleting(v => !v)}>{completing ? 'Düzenlemeyi kapat' : 'Projeyi düzenle'}</button>
      <Link className="action-secondary mb-4 ml-2" to={`/organizer/home?project=${encodeURIComponent(project.id)}`} data-testid="project-chat-link">Sohbete dön</Link>
      {completing && <ProjectCompletionForm key={project.id} project={project} missing={missing} onSaved={p => { setProject(p); setCompleting(false); }} />}
      <EventPublication key={project.id} project={project} onChange={setProject} />
      <div className="bg-white border border-zinc-200 rounded-2xl p-6 sm:p-8">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h1 className="font-serif text-3xl tracking-tight text-zinc-900" data-testid="project-detail-title">{project.title}</h1>
          <span className="text-xs font-medium px-2.5 py-1 rounded-full border bg-[#E6EEFF] text-[#1E3A8A] border-[#1E3A8A]/20 shrink-0" data-testid="project-detail-stage">
            {STAGE_LABELS[project.stage] || project.stage}
          </span>
        </div>
        {project.description && <p className="text-sm text-zinc-600 leading-relaxed mb-6" data-testid="project-detail-description">{project.description}</p>}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-5 border-t border-zinc-100">
          <Field icon={Tag} label="Etkinlik türü" value={project.event_type ? (EVENT_TYPE_LABELS[project.event_type] || project.event_type) : null} testId="project-detail-event-type" />
          <Field icon={Users} label="Kapasite" value={project.expected_attendees ? `${project.expected_attendees} kişi` : null} testId="project-detail-capacity" />
          <Field icon={Ticket} label="Ort. bilet" value={project.avg_ticket_price ? `${project.avg_ticket_price} TL` : null} testId="project-detail-price" />
          <Field icon={MapPin} label="İlçeler" value={project.preferred_districts?.length ? project.preferred_districts.join(', ') : null} testId="project-detail-districts" />
        </div>

        <div className="mt-6 pt-5 border-t border-zinc-100" data-testid="project-detail-matching">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 flex items-center gap-1 mb-1"><Layers size={11} /> {MATCH_FEATURE_NAME} durumu</p>
          {m ? (
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <p className="text-sm text-zinc-800" data-testid="project-detail-matching-status">
                  {STATUS_LABELS?.[m.status] || m.status}
                  {m.readiness_score != null && <span className="text-zinc-500"> · hazırlık %{m.readiness_score}</span>}
                  {m.active === false && <span className="text-zinc-500"> · arşivde</span>}
                </p>
                {needsCompletion ? (
                  <button type="button" onClick={() => setCompleting(v => !v)}
                    className="sm:ml-auto h-10 px-4 rounded-lg bg-amber-500 text-white text-sm font-medium inline-flex items-center justify-center gap-2 hover:bg-amber-600 transition-colors"
                    data-testid="project-detail-complete-cta">
                    <ClipboardList size={15} /> {completing ? 'Formu Gizle' : 'Eksikleri Tamamla'}
                  </button>
                ) : (
                  <button type="button" onClick={() => goToMatching(project)}
                    className="sm:ml-auto h-10 px-4 rounded-lg bg-[#1E3A8A] text-white text-sm font-medium inline-flex items-center justify-center gap-2 hover:bg-[#16307A] transition-colors"
                    data-testid="project-detail-continue-matching">
                    Mekân aramaya devam et <ArrowRight size={15} />
                  </button>
                )}
              </div>
              {needsCompletion && missing.length > 0 && (
                <p className="text-xs text-amber-700 mt-2" data-testid="project-detail-missing">
                  Eksik: {missing.map(f => FIELD_LABELS[f] || f).join(', ')}
                </p>
              )}

            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <p className="text-sm text-zinc-500">Bu proje için mekân aramaya başlayabilirsin.</p>
              <button type="button" onClick={sendToMatching} disabled={sending}
                className="sm:ml-auto h-10 px-4 rounded-lg bg-[#1E3A8A] text-white text-sm font-medium inline-flex items-center justify-center gap-2 hover:bg-[#16307A] disabled:opacity-60 transition-colors"
                data-testid="project-detail-send-matching">
                {sending ? <Loader2 size={15} className="animate-spin" /> : null} Bu etkinlik için mekân bul <ArrowRight size={15} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizerProjectDetail;
